// PWA Install Prompt Component
// Shows a notification to users about installing the app as a PWA

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Sparkles, Smartphone } from 'lucide-react';

export const InstallPrompt = ({ show = true }) => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    // Check if already installed
    useEffect(() => {
        // Check if running in standalone mode (already installed)
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone === true;

        if (isStandalone) {
            setIsInstalled(true);
            return;
        }

        // Check localStorage for dismissed state
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
        const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

        // Show again after 7 days
        if (dismissed && daysSinceDismissed < 7) {
            return;
        }

        // Listen for the beforeinstallprompt event
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            // Delay showing prompt slightly for better UX
            setTimeout(() => setIsVisible(true), 2000);
        };

        // Listen for app installed event
        const handleAppInstalled = () => {
            setIsInstalled(true);
            setIsVisible(false);
            setShowSuccess(true);
            localStorage.removeItem('pwa-install-dismissed');
            setTimeout(() => setShowSuccess(false), 3000);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstall = useCallback(async () => {
        if (!deferredPrompt) return;

        try {
            // Show the native install prompt
            deferredPrompt.prompt();

            // Wait for user choice
            const { outcome } = await deferredPrompt.userChoice;

            if (outcome === 'accepted') {
                setIsVisible(false);
                setShowSuccess(true);
                setTimeout(() => setShowSuccess(false), 3000);
            }

            // Clear the deferred prompt
            setDeferredPrompt(null);
        } catch (error) {
            console.error('Install error:', error);
        }
    }, [deferredPrompt]);

    const handleDismiss = useCallback(() => {
        setIsVisible(false);
        localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    }, []);

    // Don't show if not on home screen or already installed
    if (!show || isInstalled) return null;

    return (
        <>
            {/* Install Prompt */}
            <AnimatePresence>
                {isVisible && deferredPrompt && (
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="fixed bottom-20 left-4 right-4 z-50 max-w-md mx-auto"
                    >
                        <div className="install-prompt-card relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-purple-900/95 via-indigo-900/95 to-purple-900/95 backdrop-blur-xl shadow-2xl shadow-purple-900/50">
                            {/* Decorative glow */}
                            <div className="absolute -top-20 -right-20 w-40 h-40 bg-amber-500/20 rounded-full blur-3xl" />
                            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl" />

                            {/* Close button */}
                            <button
                                onClick={handleDismiss}
                                className="absolute top-2 right-2 p-3 z-20 text-amber-200/60 hover:text-amber-100 hover:bg-white/10 rounded-full transition-all"
                                aria-label="Dismiss"
                            >
                                <X className="w-5 h-5" />
                            </button>

                            <div className="relative p-5">
                                {/* Icon and sparkles */}
                                <div className="flex items-start gap-4">
                                    <motion.div
                                        className="flex-shrink-0 p-3 rounded-xl bg-gradient-to-br from-amber-500/20 to-purple-500/20 border border-amber-500/30"
                                        animate={{ rotate: [0, 5, -5, 0] }}
                                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                    >
                                        <Smartphone className="w-8 h-8 text-amber-300" />
                                    </motion.div>

                                    <div className="flex-1 pr-6">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="text-lg font-serif font-semibold text-amber-100">
                                                Install Cosmic Compass
                                            </h3>
                                            <motion.div
                                                animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.1, 1] }}
                                                transition={{ duration: 2, repeat: Infinity }}
                                            >
                                                <Sparkles className="w-4 h-4 text-amber-400" />
                                            </motion.div>
                                        </div>
                                        <p className="text-sm text-purple-200/80 leading-relaxed">
                                            Add to your home screen for instant access to mystical guidance, even offline.
                                        </p>
                                    </div>
                                </div>

                                {/* Install button */}
                                <motion.button
                                    onClick={handleInstall}
                                    className="mt-4 w-full py-3 px-6 rounded-xl font-medium text-base flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-purple-950 hover:from-amber-400 hover:to-amber-500 shadow-lg shadow-amber-500/30 transition-all"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <Download className="w-5 h-5" />
                                    <span>Install App</span>
                                </motion.button>

                                {/* Subtle hint */}
                                <p className="mt-3 text-xs text-center text-purple-300/50">
                                    Free • No app store required
                                </p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Success Toast */}
            <AnimatePresence>
                {showSuccess && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="fixed bottom-24 left-4 right-4 z-50 max-w-sm mx-auto"
                    >
                        <div className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-green-500/90 to-emerald-500/90 backdrop-blur-sm shadow-xl shadow-green-500/30 text-white">
                            <motion.div
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ duration: 0.5, repeat: 2 }}
                            >
                                <Sparkles className="w-6 h-6" />
                            </motion.div>
                            <span className="font-medium">Cosmic Compass installed! ✨</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default InstallPrompt;
